/** App-wide UI state: toasts, theme and the business time zone. */
import type { ToastItem } from '@ff/app-ui';
import { createStore } from './store.ts';

export const toasts = createStore<ToastItem[]>([]);
let seq = 1;

export function toast(text: string, action?: { label: string; run: () => void }, ms = 4000): void {
  const id = seq++;
  toasts.set((t) => [...t.slice(-2), { id, text, actionLabel: action?.label, onAction: action?.run }]);
  // Toasts with an Undo stay long enough to use it (10 seconds for review requests, docs/52 S42).
  setTimeout(() => dismissToast(id), action ? Math.max(ms, 10_000) : ms);
}
export function dismissToast(id: number): void {
  toasts.set((t) => t.filter((x) => x.id !== id));
}

export type ThemePref = 'system' | 'light' | 'dark';
export const theme = createStore<ThemePref>(readTheme());

function readTheme(): ThemePref {
  try {
    const v = localStorage.getItem('ff-theme');
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

export function applyTheme(pref: ThemePref): void {
  theme.set(pref);
  try {
    if (pref === 'system') localStorage.removeItem('ff-theme');
    else localStorage.setItem('ff-theme', pref);
  } catch {
    /* private mode */
  }
  if (pref === 'system') delete document.documentElement.dataset.theme;
  else document.documentElement.dataset.theme = pref;
  const dark = pref === 'dark' || (pref === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', dark ? '#0b1f14' : '#0e2a1c');
}

/** The business's zone for every date and time (docs/52 §7.3). Set from Home or the location. */
export const tz = createStore<string>('America/Chicago');

/** One short haptic on devices that support it (docs/52 §4.6). */
export function haptic(): void {
  try {
    navigator.vibrate?.(12);
  } catch {
    /* unsupported */
  }
}
